import './CartProductVariants.scss'

import {useMediaQuery} from '@material-ui/core'
import React, {useEffect, useState} from 'react'
import {Flipped} from 'react-flip-toolkit'
import {hashProduct, Meta, Product, useCart} from '../../../../util/cart'
import {useTranslation} from '../../../../util/i18n'
import {getPriceDiscount} from '../../../../util/price'
import {style} from '../../../../util/style'
import {Theme} from '../../../Theme'
import {ProductQuantityDiscount, ProductVariationType} from '../../pages'
import {ProductQuantity} from '../product/ProductQuantity'
import {ProductVariants} from '../product/ProductVariants'
import {CartProductVariantFlipped, ProductCartType} from './CartProduct'
import {CartProductPrice} from './CartProductPrice'

const styles = style('cartproductvariants', ['variant'])

export type CartProductVariantType = {
	uniqueKey: string
	variant: Product
	variations: Array<ProductVariationType>
	discounts: Array<ProductQuantityDiscount>
	price?: string
	productId: string
	digital?: boolean
	flippedProps?: any
}

export const CartProductVariants: React.FC<ProductCartType> = ({
	productVariants,
	variations,
	price,
	quantity_discount: discounts,
	channel,
	...product
}) => {
	const {cart} = useCart()
	// only show variations that are not allready in cart
	const filteredVariations = filterVariationsByIdsAllreadyUsed(
		variations,
		productVariants[0].id,
		cart.productsPerId,
		null
	)
	const hasVariations = variations && variations.length > 0
	const hasFilteredVariations = variations && variations.length > 0

	let showAddVariant = true
	if (hasFilteredVariations) {
		filteredVariations.forEach((variation) => {
			if (variation.filteredOptions?.length === 0) showAddVariant = false
		})
	}

	return (
		<Flipped inverseFlipId={'product_flipper_' + product.id}>
			<div className={styles}>
				{productVariants.map((variant) => {
					const variantKey = hashProduct(variant) || variant.id
					return (
						<CartProductVariantFlipped
							key={variant.randomKey || product.id}
							uniqueKey={variantKey}
							variations={variations}
							discounts={discounts}
							variant={variant}
							price={price}
							productId={variant.id}
							digital={channel === 'product_detail_downloadable'}
						/>
					)
				})}
				{showAddVariant && hasVariations && (
					<CartProductVariantFlipped
						uniqueKey={variations[0].id + 'empty'}
						variations={variations}
						discounts={discounts}
						variant={null}
						productId={productVariants[0].id}
					/>
				)}
			</div>
		</Flipped>
	)
}

export const CartProductVariant: React.FC<CartProductVariantType> = ({
	variations,
	variant,
	discounts,
	price = null,
	productId,
	digital: digitalProduct,
	uniqueKey,
	flippedProps
}) => {
	// states
	const [addProductAllowed, setAddProductAllowed] = useState<boolean>(false)
	const [quantity, setQuantity] = useState<number>()
	const [chosenMeta, setChosenMeta] = useState<Meta>()
	const [isSaving, setSaving] = useState<boolean>()
	// type of variant
	const addNewVariantType = !variant
	const t = useTranslation('webshop').cart

	// cart variables we need
	const {cart, setQuantityAndMeta, removeProduct, addProduct} = useCart()
	const cartQuantity = (variant && cart.quantityOf(variant)) || 1
	const cartMeta = !addNewVariantType && getDefaultMeta(variations, variant)
	const totalProductVariants = cart.quantityOfVariantsWith(productId)

	const isMobile = useMediaQuery('(max-width:600px)')

	// only show variations that are not allready in cart
	const filteredVariations = filterVariationsByIdsAllreadyUsed(
		variations,
		productId,
		cart.productsPerId,
		variant
	)
	const cartMetaChanged =
		JSON.stringify(cartMeta) !== JSON.stringify(chosenMeta)
	// fill in meta from cart
	useEffect(() => {
		let editTimeout
		if (isSaving) {
			editTimeout = setTimeout(() => {
				setSaving(false)
			}, 1000)
		}

		return () => clearTimeout(editTimeout)
	}, [isSaving])

	useEffect(() => {
		if (quantity !== cartQuantity) {
			setQuantity(cartQuantity)
		}
	}, [cartQuantity])

	// discount
	const priceAfterDiscount = getPriceDiscount(discounts, totalProductVariants)
		?.price_per_piece

	// other
	const hasVariations = variations?.length > 0
	const disableButton =
		!hasVariations || !addProductAllowed || digitalProduct || isSaving
	const disableQuantity =
		digitalProduct || (addNewVariantType && !addProductAllowed)

	return (
		<div className={styles.variant} {...flippedProps}>
			<div className={styles.variant('row')}>
				<div
					className={styles.variant('row-left').mod({new: addNewVariantType})}
				>
					<div className={styles.variant('row-left-edit')}>
						<span
							className={styles.variant('row-left-edit-item').mod('quantity')}
						>
							<ProductQuantity
								uniqueKey={uniqueKey}
								quantity={quantity || cartQuantity}
								max={disableQuantity ? 1 : 9999}
								setQuantity={(q) => {
									const quantityChanged = q !== cartQuantity
									setQuantity(q)
									if (hasVariations) {
										setAddProductAllowed(quantityChanged || cartMetaChanged)
									} else {
										setAddProductAllowed(quantityChanged)
									}
								}}
							/>
						</span>
						{hasVariations && (
							<span
								className={styles.variant('row-left-edit-item').mod('variants')}
							>
								<ProductVariants
									meta={
										!addNewVariantType
											? chosenMeta || cartMeta || null
											: chosenMeta || null
									}
									onChange={(typeid, optionid) => {
										const differentFromCart =
											JSON.stringify({[typeid]: optionid}) !==
											JSON.stringify(cartMeta)
										setChosenMeta({[typeid]: optionid})
										setAddProductAllowed(differentFromCart)
									}}
									variations={filteredVariations}
									inCart
								/>
							</span>
						)}
						<span
							className={styles.variant('row-left-edit-item').mod(['edit'])}
						>
							<CartProductVariantsButton
								addNewVariantType={addNewVariantType}
								disableButton={
									addNewVariantType
										? disableButton
										: !addProductAllowed || isSaving
								}
								isSaving={isSaving}
								addProductAllowed={addProductAllowed}
								onClick={() => {
									if (addNewVariantType) {
										if (
											typeof chosenMeta !== 'undefined' &&
											Object.keys(chosenMeta).length !== 0
										) {
											addProduct({id: productId, meta: chosenMeta}, quantity)
										}
										setChosenMeta({})
									} else {
										setQuantityAndMeta(variant, quantity, chosenMeta)
									}
									setAddProductAllowed(false)
									setSaving(true)
								}}
							/>
						</span>
					</div>
					{!addNewVariantType && (
						<div className={styles.variant('row-left-control')}>
							<div className={styles.variant('row-left-control-remove')}>
								<Theme.Link
									href="#"
									onClick={(e) => {
										e.preventDefault()
										removeProduct(variant)
									}}
								>
									{t.remove}
								</Theme.Link>
							</div>
							{isMobile && (
								<CartProductPrice
									price={price}
									priceAfterDiscount={priceAfterDiscount}
									totalProduct={totalProductVariants}
									quantity={quantity}
									alignLeft
								/>
							)}
						</div>
					)}
				</div>
				{!isMobile && !addNewVariantType && (
					<CartProductPrice
						price={price}
						priceAfterDiscount={priceAfterDiscount}
						totalProduct={totalProductVariants}
						quantity={cartQuantity}
					/>
				)}
			</div>
		</div>
	)
}
const getDefaultMeta = (
	variations: Array<ProductVariationType>,
	variant: Product
) => {
	let defaultMeta = {}
	if (!variations || variations.length === 0) return defaultMeta
	if (!variant.meta) return defaultMeta
	variations.forEach(({id, options}) => {
		defaultMeta = {
			...defaultMeta,
			[id]: options.find((option) => option?.id === variant.meta[id])?.id
		}
	})
	return defaultMeta
}

const filterVariationsByIdsAllreadyUsed = (
	variations: Array<ProductVariationType>,
	productId: string,
	productsPerId: Array<{productId: string; variants: Array<Product>}>,
	variant?: Product
): Array<ProductVariationType> => {
	const usedOptions = productsPerId.find(
		(product) => product.productId === productId
	)?.variants
	const hasVariations = variations && variations.length > 0

	return (
		hasVariations &&
		variations.map((variation) => {
			const options = variation?.options
			const filteredOptions = options.filter((option) => {
				if (!usedOptions) return false
				if (
					variant &&
					variant.meta &&
					option.id === Object.values(variant.meta)[0]
				)
					return true
				return !usedOptions.find((uOption) => {
					if (!uOption) return false
					if (!uOption.meta) return false
					return Object.values(uOption.meta).includes(option.id)
				})
			})
			variation.filteredOptions = filteredOptions
			return variation
		})
	)
}

export const CartProductVariantsButton: React.FC<{
	addNewVariantType: boolean
	disableButton: boolean
	isSaving: boolean
	addProductAllowed: boolean
	onClick: () => void
}> = ({addNewVariantType, disableButton, isSaving, onClick}) => {
	const t = useTranslation('webshop').cart
	const copyAdded = isSaving ? t.added : t.add
	const copyEdited = isSaving ? t.edited : t.edit

	return (
		<Theme.Button
			mod={['lightgreybg', 'noicon', 'noborder']}
			disabled={disableButton}
			onClick={onClick}
			classPrefix={styles
				.variant('row-left-edit-item-button')
				.is({saving: isSaving})}
		>
			{addNewVariantType ? copyAdded : copyEdited}
		</Theme.Button>
	)
}
